import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, LayoutGrid } from 'lucide-react';
import { formatCategoryName } from '../utils/category-format';

// Kept within the rail's side padding so a snapped item is never faded
const EDGE_FADE = 24;

// Soft backgrounds for categories that have no photo yet
const TONES = [
  'bg-[#e4ebe6] text-[#174540]',
  'bg-[#efe4d6] text-[#7a5a3c]',
  'bg-[#f2e3dd] text-[#8a4b3f]',
  'bg-[#e8e6e1] text-[#3a4a47]',
];

const CategorySection = ({ selectedCategory, setSelectedCategory, brands = [], onSelectCategory }) => {
  // Create categories from brands plus an "all" option
  const categories = [
    { id: 'all', name: 'Tous les produits', imagePath: null },
    ...brands.map(brand => ({
      id: String(brand.id ?? brand.Id ?? ''),
      name: brand.name ?? brand.Name ?? '',
      imagePath: brand.imagePath ?? brand.ImagePath ?? null
    }))
  ];

  const railRef = useRef(null);
  const [rail, setRail] = useState({ canPrev: false, canNext: false });

  const updateRail = useCallback(() => {
    const node = railRef.current;
    if (!node) return;
    const max = node.scrollWidth - node.clientWidth;
    setRail({ canPrev: node.scrollLeft > 4, canNext: node.scrollLeft < max - 4 });
  }, []);

  useEffect(() => {
    const node = railRef.current;
    if (!node) return undefined;
    updateRail();
    node.addEventListener('scroll', updateRail, { passive: true });
    window.addEventListener('resize', updateRail);
    return () => {
      node.removeEventListener('scroll', updateRail);
      window.removeEventListener('resize', updateRail);
    };
  }, [updateRail, brands.length]);

  // Bring the selected category into view (e.g. when picked from the hero)
  useEffect(() => {
    const node = railRef.current;
    const item = node?.querySelector(`[data-category-id="${String(selectedCategory)}"]`);
    if (!node || !item) return;
    const outOfView =
      item.offsetLeft < node.scrollLeft ||
      item.offsetLeft + item.offsetWidth > node.scrollLeft + node.clientWidth;
    if (outOfView) {
      node.scrollTo({ left: item.offsetLeft - (node.clientWidth - item.offsetWidth) / 2, behavior: 'smooth' });
    }
  }, [selectedCategory, brands.length]);

  const scrollRail = (direction) => {
    const node = railRef.current;
    if (!node) return;
    node.scrollBy({ left: direction * node.clientWidth * 0.7, behavior: 'smooth' });
  };

  // When category (brand) buttons are clicked
  const handleCategoryClick = (brandId) => {
    // Update the selected category in the UI
    setSelectedCategory(String(brandId));

    // Call the API with the appropriate filter parameter
    if (typeof onSelectCategory === 'function') {
      if (brandId === 'all') {
        // For "Tous les produits", call with null or undefined marqueId
        // This ensures the parameter is completely removed from the API request
        onSelectCategory({
          marqueId: null, // explicitly set to null to clear it
          category: null, // also clear any category filter that might be set
        });
      } else {
        onSelectCategory({
          marqueId: brandId
        });
      }
    }
  };

  // Fade only the edges that still hide more categories
  const railMask = `linear-gradient(to right, ${rail.canPrev ? 'transparent' : '#000'} 0, #000 ${EDGE_FADE}px, #000 calc(100% - ${EDGE_FADE}px), ${rail.canNext ? 'transparent' : '#000'} 100%)`;

  const arrowClass =
    'flex h-10 w-10 items-center justify-center rounded-full border border-[#0c2a27]/15 bg-white text-[#0c2a27] transition-colors duration-300 hover:border-[#0c2a27]/40 disabled:cursor-default disabled:opacity-30';

  let toneIndex = 0;

  return (
    <section id="categories" className="scroll-mt-24 bg-[#faf7f2] py-12 sm:py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-3 text-[0.68rem] font-semibold uppercase tracking-[0.3em] text-[#3d8361]">
              <span className="h-px w-8 bg-[#3d8361]/50" />
              Catégories
            </span>
            <h2 className="mb-0 mt-3 text-left font-serif text-3xl font-medium text-[#0c2a27] sm:text-4xl">
              Explorer par <span className="italic text-[#174540]">catégorie</span>
            </h2>
          </div>

          {(rail.canPrev || rail.canNext) && (
            <div className="hidden flex-none items-center gap-2 sm:flex">
              <button type="button" onClick={() => scrollRail(-1)} disabled={!rail.canPrev} aria-label="Catégories précédentes" className={arrowClass}>
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button type="button" onClick={() => scrollRail(1)} disabled={!rail.canNext} aria-label="Catégories suivantes" className={arrowClass}>
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>

        {/* One line, scrolls horizontally */}
        <div
          ref={railRef}
          className="tdr-no-scrollbar relative -mx-4 flex snap-x gap-2 overflow-x-auto scroll-px-4 px-4 py-2 sm:-mx-6 sm:scroll-px-6 sm:gap-4 sm:px-6 lg:-mx-8 lg:scroll-px-8 lg:px-8"
          style={{ WebkitMaskImage: railMask, maskImage: railMask }}
        >
          {categories.map((category, i) => {
            const isActive = String(selectedCategory) === String(category.id);
            const isAll = category.id === 'all';
            const label = isAll ? category.name : formatCategoryName(category.name);
            const tone = !isAll && !category.imagePath ? TONES[toneIndex++ % TONES.length] : null;

            return (
              // Each item animates on its own mount, so categories that arrive late still show up
              <motion.button
                key={category.id}
                type="button"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: Math.min(i, 12) * 0.04, ease: [0.22, 1, 0.36, 1] }}
                data-category-id={category.id}
                title={label}
                aria-pressed={isActive}
                onClick={() => handleCategoryClick(category.id)}
                className="group flex w-[5.5rem] flex-none snap-start flex-col items-center text-center focus:outline-none sm:w-[6.5rem]"
              >
                <span
                  className={`flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-full p-[2.5px] transition-colors duration-300 group-focus-visible:ring-2 group-focus-visible:ring-[#174540]/40 sm:h-[5.25rem] sm:w-[5.25rem]
                    ${isActive ? 'bg-gradient-to-tr from-[#0c2a27] via-[#3d8361] to-[#c9a078]' : 'bg-[#e6dccf] group-hover:bg-[#174540]/50'}`}
                >
                  <span
                    className={`relative flex h-full w-full items-center justify-center overflow-hidden rounded-full border-[3px] border-[#faf7f2]
                      ${isAll ? 'bg-[#174540] text-[#e6d3bd]' : tone || 'bg-[#efe4d6]'}`}
                  >
                    {isAll ? (
                      <LayoutGrid className="h-6 w-6" strokeWidth={1.5} />
                    ) : category.imagePath ? (
                      <img
                        src={category.imagePath}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <span className="font-serif text-3xl italic leading-none">{label.charAt(0)}</span>
                    )}
                  </span>
                </span>

                <span
                  className={`mt-2 line-clamp-3 text-[0.72rem] leading-snug sm:text-xs
                    ${isActive ? 'font-semibold text-[#174540]' : 'font-medium text-[#3a4a47] group-hover:text-[#0c2a27]'}`}
                >
                  {label}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CategorySection;
